import { useCallback, useEffect, useMemo } from 'react';
import { Alert, App, Button, Card, Col, ConfigProvider, Divider, Flex, Row, Space, Tag, theme, Tooltip, Typography } from 'antd';
import { CalendarOutlined } from '@ant-design/icons';
import { useQueryClient, useQueries } from '@tanstack/react-query';
import { agreementsQueryKeys, useGetAgreement, useRestoreAgreement } from '@/features/agreements/hooks/useAgreements';
import { useGetPlans } from '@/features/agreements/hooks/usePlans';
import { getRates } from '@/features/agreements/services/ratesService';
import { ratesQueryKeys } from '@/features/agreements/hooks/useRates';
import { getCoverageConfigs } from '@/features/quotations/services/coverageConfigService';
import { coverageConfigQueryKeys } from '@/features/quotations/hooks/useCoverageConfigs';
import { SectionHeader } from '../SectionHeader';
import { LoadingIndicator } from '@/shared/components/LoadingIndicator';
import { ErrorMessage } from '@/shared/components/ErrorMessage';
import { getErrorMessage } from '@/shared/utils/errors/getErrorMessage';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPen } from '@fortawesome/free-solid-svg-icons';
import { RulesStepIcon } from '@/features/agreements/icons/RulesStepIcon';
import { CoveragesStepIcon } from '@/features/agreements/icons/CoveragesStepIcon';
import { BasicInfoStepIcon } from '@/features/agreements/icons/BasicInfoStepIcon';
import { PlansStepIcon } from '@/features/agreements/icons/PlansStepIcon';
import { CalculationStepIcon } from '@/features/agreements/icons/CalculationStepIcon';
import { ExpressionSummaryDisplay } from '../CalculationsStep/utils/ExpressionSummaryDisplay';
import { formatDateForDisplay } from '@shared/utils/dateUtils';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '@/App/Router/routes';

const { Text } = Typography;

const SUMMARY_STEP_INDEX = {
  basicInfo: 0,
  plans: 1,
  coverages: 2,
  rules: 3,
  calculations: 4,
};

export const SummaryStep = ({ agreementId, onNextRef, onGoToStep, readOnly = false, action }) => {
  const navigate = useNavigate();

  const { token: themeToken } = theme.useToken();
  const { notification } = App.useApp();
  const queryClient = useQueryClient();
  const agreementQuery = useGetAgreement(agreementId);
  const plansQuery = useGetPlans({ agreementId });

  const agreement = agreementQuery.data;
  const plans = plansQuery.data ?? [];
  const hasPlans = plans.length > 0;
  const premiumCalculation = (agreement?.calculations ?? []).find((c) => c.calculationType === 'premium');
  const hasPremiumCalculation = !!premiumCalculation;

  const coverageConfigsQueries = useQueries({
    queries: plans.map((plan) => ({
      queryKey: coverageConfigQueryKeys.list({ planId: plan.id, active: true }),
      queryFn: () => getCoverageConfigs({ planId: plan.id, active: true }),
      enabled: hasPlans,
    })),
  });

  const ratesQueries = useQueries({
    queries: plans.map((plan) => ({
      queryKey: ratesQueryKeys.list({ agreementId, planId: plan.id }),
      queryFn: () => getRates(agreementId, plan.id),
      enabled: !!agreementId && hasPlans,
    })),
  });

  const restoreAgreementMutation = useRestoreAgreement();

  const isComplete = useMemo(() => {
    if (!agreement?.name || !agreement?.insuranceCarrier || !agreement?.startDate) {
      return false;
    }
    if (plans.length === 0) return false;
    if (!hasPremiumCalculation) return false;
    const allPlansHaveCoverages = coverageConfigsQueries.every(
      (q) => q.data !== undefined && q.data?.length > 0
    );
    const allPlansHaveRates = ratesQueries.every(
      (q) => q.data !== undefined && q.data?.length > 0
    );
    return allPlansHaveCoverages && allPlansHaveRates;
  }, [agreement, plans.length, hasPremiumCalculation, coverageConfigsQueries, ratesQueries]);

  const incompleteMessage = useMemo(() => {
    if (!agreement?.name || !agreement?.insuranceCarrier || !agreement?.startDate) {
      return 'Completa la información básica del convenio.';
    }
    if (plans.length === 0) return 'Agrega al menos un plan.';
    if (!hasPremiumCalculation) return 'Configura el cálculo de prima.';
    const plansWithoutCoverages = plans.filter((_, i) => !(coverageConfigsQueries[i]?.data?.length > 0));
    if (plansWithoutCoverages.length > 0) {
      return `Faltan coberturas en: ${plansWithoutCoverages.map((p) => p.name).join(', ')}.`;
    }
    const plansWithoutRates = plans.filter((_, i) => !(ratesQueries[i]?.data?.length > 0));
    if (plansWithoutRates.length > 0) {
      return `Faltan tasas en: ${plansWithoutRates.map((p) => p.name).join(', ')}.`;
    }
    return '';
  }, [agreement, plans, hasPremiumCalculation, coverageConfigsQueries, ratesQueries]);

  const handleNext = useCallback(async () => {
    if (readOnly) {
      navigate(ROUTES.ADMIN_PANEL);
      return;
    }

    if (!isComplete) {
      notification.error({
        message: <Text strong>Convenio incompleto</Text>,
        description: incompleteMessage || 'Revisa que todas las configuraciones estén completas.',
      });
      return false;
    }

    const shouldActivate =
      action === 'create' || (action === 'update' && !agreement?.active);

    if (!shouldActivate) {
      navigate(ROUTES.ADMIN_PANEL);
      return;
    }

    try {
      await restoreAgreementMutation.mutateAsync({ id: agreementId });
      void queryClient.invalidateQueries({ queryKey: agreementsQueryKeys.detail(agreementId) });
      notification.success({
        message: <Text strong>Convenio activado</Text>,
        description: '¡El convenio se ha activado correctamente!',
      });
      navigate(ROUTES.ADMIN_PANEL);
    } catch (error) {
      notification.error({
        message: <Text strong>Convenio no activado</Text>,
        description: getErrorMessage(error),
      });
      return false;
    }
  }, [
    readOnly,
    action,
    agreement?.active,
    isComplete,
    incompleteMessage,
    agreementId,
    notification,
    queryClient,
    restoreAgreementMutation,
    navigate,
  ]);

  useEffect(() => {
    if (!onNextRef) return;
    onNextRef.current = handleNext;
    return () => {
      onNextRef.current = null;
    };
  }, [handleNext, onNextRef]);

  const isLoadingSummaryData = (
    coverageConfigsQueries.some(q => q.isLoading) ||
    ratesQueries.some(q => q.isLoading) ||
    agreementQuery.isLoading ||
    plansQuery.isLoading
  );

  if (isLoadingSummaryData) return <LoadingIndicator />;
  if (agreementQuery.isError) return <ErrorMessage error={agreementQuery.error} />;
  if (plansQuery.isError) return <ErrorMessage error={plansQuery.error} />;
  if (!agreement) return null;

  const vigencia = `${formatDateForDisplay(agreement.startDate)}${agreement.endDate ? ` - ${formatDateForDisplay(agreement.endDate)}` : ''}`;

  const renderCardExtra = (stepIndex) => {
    if (readOnly) return null;
    return (
      <ConfigProvider
        theme={{
          components: {
            Button: {
              colorLink: '#595959',
              algorithm: true,
            },
          },
        }}
      >
        <Tooltip title='Editar'>
          <Button
            type='link'
            size='small'
            icon={<FontAwesomeIcon icon={faPen} />}
            onClick={() => onGoToStep?.(stepIndex)}
          />
        </Tooltip>
      </ConfigProvider>
    );
  };

  const cardStyle = { flex: '1 1 45%', minWidth: 280 };

  return (
    <Flex justify='center' style={{ paddingInline: '5%' }}>
      <Flex vertical gap='large' flex={1} style={{ width: '100%' }}>
        <Flex justify='space-between' align='flex-start'>

          <SectionHeader
            title='Resumen del convenio'
            description='Revisa que todas las configuraciones estén completas antes de activar el convenio.'
          />

          <Alert
            type={isComplete ? 'success' : 'warning'}
            showIcon
            message={
              isComplete ? (
                <span>Convenio listo para activar</span>
              ) : (
                <span>Incompleto</span>
              )
            }
            description={isComplete ? null : (incompleteMessage || 'Completa planes, coberturas y tasas por plan.')}
            closable

          />
        </Flex>

        <Flex gap='large' wrap='wrap'>
          <Card
            title={
              <Space size='small'>
                <BasicInfoStepIcon />
                <span>Información básica</span>
              </Space>
            }
            extra={renderCardExtra(SUMMARY_STEP_INDEX.basicInfo)}
            style={cardStyle}
            styles={{
              header: { color: themeToken.colorPrimary }
            }}
          >
            <Row gutter={[8, 8]}>
              <Col span={12}>
                <Flex vertical>
                  <Text type='secondary' style={{ fontSize: '0.9em' }}>Aseguradora</Text>
                  <Text>{agreement.insuranceCarrier?.name ?? '-'}</Text>
                </Flex>
              </Col>
              <Col span={12}>
                <Flex vertical>
                  <Text type='secondary' style={{ fontSize: '0.9em' }}>Nombre del convenio</Text>
                  <Text>{agreement.name ?? '-'}</Text>
                </Flex>
              </Col>
              <Col span={24}>
                <Divider size='middle' />
              </Col>
              <Col span={24}>
                <Flex vertical>
                  <Text type='secondary' style={{ fontSize: '0.9em' }}>Vigencia</Text>
                  <Text>
                    <Space>
                      <CalendarOutlined />
                      {vigencia}
                    </Space>
                  </Text>
                </Flex>
              </Col>
            </Row>
          </Card>

          <Card
            title={
              <Space size='small'>
                <PlansStepIcon />
                <span>Planes</span>
              </Space>
            }
            extra={renderCardExtra(SUMMARY_STEP_INDEX.plans)}
            style={cardStyle}
            styles={{
              header: { color: themeToken.colorPrimary }
            }}
          >
            {plans.length === 0 ? (
              <Text type='secondary'>No hay planes configurados.</Text>
            ) : (
              <Flex vertical>
                {plans.map((plan, index) => {
                  const coverageCount = coverageConfigsQueries[index]?.data?.length ?? 0;
                  const ratesCount = ratesQueries[index]?.data?.length ?? 0;
                  return (
                    <Card
                      key={plan.id}
                      size='small'
                      style={{
                        paddingInline: '3%',
                        marginTop: -1,
                        borderColor: '#d9d9d9',
                        backgroundColor: 'transparent',
                      }}>
                      <Flex vertical>
                        <Space>
                          <Text>{plan.name}</Text>
                          <Tag color={plan.active ? 'green' : 'red'}>
                            {plan.active ? 'Activo' : 'Inactivo'}
                          </Tag>
                        </Space>
                        <Space size='middle' style={{ fontSize: '0.9em' }}>
                          <Text type='secondary' style={{ fontSize: 'inherit' }}>
                            {ratesCount} {ratesCount === 1 ? 'tasa' : 'tasas'}
                          </Text>
                          <Text type='secondary' style={{ fontSize: 'inherit' }}>•</Text>
                          <Text type='secondary' style={{ fontSize: 'inherit' }}>
                            {coverageCount} {coverageCount === 1 ? 'cobertura' : 'coberturas'}
                          </Text>
                        </Space>
                      </Flex>
                    </Card>
                  );
                })}
              </Flex>
            )}
          </Card>

          <Card
            title={
              <Space size='small'>
                <CoveragesStepIcon />
                <span>Coberturas</span>
              </Space>
            }
            extra={renderCardExtra(SUMMARY_STEP_INDEX.coverages)}
            style={cardStyle}
            styles={{
              header: { color: themeToken.colorPrimary }
            }}
          >
            {plans.length === 0 ? (
              <Text type='secondary'>No hay planes.</Text>
            ) : (
              <Flex vertical gap='middle'>
                {plans.map((plan, index) => {
                  const configs = coverageConfigsQueries[index]?.data ?? [];
                  return (
                    <div key={plan.id}>
                      <Text style={{ display: 'block', marginBottom: 4 }}>
                        {plan.name}
                      </Text>
                      {configs.length === 0 ? (
                        <Text type='secondary'>Sin coberturas</Text>
                      ) : (
                        <Flex gap='small' wrap='wrap'>
                          {configs.map((c) => (
                            <Tag key={c.id}>
                              {c.coverage?.name ?? '-'}
                            </Tag>
                          ))}
                        </Flex>
                      )}
                    </div>
                  );
                })}
              </Flex>
            )}
          </Card>

          <Card
            title={
              <Space size='small'>
                <RulesStepIcon />
                <span>Tasas por plan</span>
              </Space>
            }
            extra={renderCardExtra(SUMMARY_STEP_INDEX.rules)}
            style={cardStyle}
            styles={{
              header: { color: themeToken.colorPrimary }
            }}
          >
            {plans.length === 0 ? (
              <Text type='secondary'>No hay planes.</Text>
            ) : (
              <Flex vertical gap='middle'>
                {plans.map((plan, index) => {
                  const rates = ratesQueries[index]?.data ?? [];
                  return (
                    <div key={plan.id}>
                      <Text style={{ display: 'block', marginBottom: 4 }}>
                        {plan.name}
                      </Text>
                      {rates.length === 0 ? (
                        <Text type='secondary'>Sin tasas</Text>
                      ) : (
                        <Flex gap='small' wrap='wrap'>
                          {rates.map((r) => (
                            <Tag key={r.id} color='green'>
                              {r.percentage}%
                            </Tag>
                          ))}
                        </Flex>
                      )}
                    </div>
                  );
                })}
              </Flex>
            )}
          </Card>

          <Card
            title={
              <Space size='small'>
                <CalculationStepIcon />
                <span>Cálculo de prima</span>
              </Space>
            }
            extra={renderCardExtra(SUMMARY_STEP_INDEX.calculations)}
            style={cardStyle}
            styles={{
              header: { color: themeToken.colorPrimary }
            }}
          >
            {hasPremiumCalculation ? (
              <Text><ExpressionSummaryDisplay node={premiumCalculation.expression} /></Text>
            ) : (
              <Text type='secondary'>No definido</Text>
            )}
          </Card>
        </Flex>
      </Flex>
    </Flex>
  );
};
